/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import {Product} from '@amplify/athena-models';
import {
    AppBar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Toolbar,
    Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import {runInAction, when} from 'mobx';
import {observer} from 'mobx-react-lite';
import {FC, useEffect, useMemo, useState} from 'react';
import {useHistory, useLocation, useParams} from 'react-router-dom';

import CRM, {ClientQuoteData} from '../APIs/CRM';
import CustomerPortalAPI from '../APIs/CustomerPortalAPI';
import {AgentStore} from '../Agent/AgentStore';
import BankingPanel from '../Agent/Banking/Banking';
import {ContactForm} from '../Agent/ContactForm/ContactForm';
// eslint-disable-next-line import/no-named-as-default
import {updateRecommendationProducts} from '../APIs/recommendationUtils';
import CustomerPortalSelectionDialog from '../Agent/Customer/CustomerPortalSelectionDialog';
import PolicyPanel from '../Agent/Policy/Policy';
import PreApprovalPanel from '../Agent/PreApprovals/Preapprovals';
import {PrequalPanel} from '../Agent/PreQualification/Prequalification';
import {QuotePanel} from '../Agent/Quotes/QuotesPanel';
import UnderwritingPanel from '../Agent/Underwritings/UnderwritingRequirements';
import {TabPanel} from '../Core/UI';
import {convertWalkthroughData} from '../Core/helpers/walkthrough/convertWalkthroughData';
import {Parameter} from '../Core/mobx/ParameterStore/ParameterStore';
import RootStore from '../Core/mobx/RootStore';
import {CaseManagerStore} from './CaseManagerStore';
import {Form} from './Form';

const OWNER_KEYS_MAP = {
    socialSecurityNumber: 'ownerSocialSecurityNumber',
    bmonth: 'ownerBMonth',
    bday: 'ownerBday',
    byear: 'ownerByear',
};

interface FormDataPanelProps {
    store: AgentStore | CaseManagerStore;
    selectedTabIndex: number;
}

export interface AllProductType extends Product {
    recommended?: boolean;
}

export const FormDataPanel: FC<FormDataPanelProps> = observer(({store, selectedTabIndex = 0}) => {
    const {email, formId, historyFormId} = useParams<any>();
    const {search} = useLocation();
    const {familyFormId, familyEmail} = Object.fromEntries(new URLSearchParams(search));

    const {parameterStore} = store;
    const {formDirty} = parameterStore;
    const contactStore = store.contactsFormStore;
    const [customerQuoteData, setCustomerQuoteData] = useState([] as ClientQuoteData[]);
    const [isLoading, setIsLoading] = useState(true);

    const customerPortalStore =
        'customerPortalStore' in store ? store.customerPortalStore : RootStore.agentPortalStore.customerPortalStore;

    const getForm = async () => {
        RootStore.uiStore.showSnackbar('Getting form...', 'info');
        runInAction(() => {
            parameterStore.loadingClientData = true;
        });
        await store.selectForm({id: formId, email, historyId: historyFormId});
        runInAction(() => {
            parameterStore.loadingClientData = false;
        });
        RootStore.uiStore.showSnackbar('Form ready!', 'success');
    };

    const getFormForFamily = async () => {
        RootStore.uiStore.showSnackbar('Getting form for family...', 'info');
        await store.startFamilyForm({id: familyFormId, email: familyEmail});
        RootStore.uiStore.showSnackbar('Form ready!', 'success');
    };

    // refresh only the qualified products for existing forms;
    const getQualifiedProductsInternal = async (userEmail: string) => {
        CustomerPortalAPI.GetCustomerQuotes(userEmail).then((quoteData) => setCustomerQuoteData(quoteData || []));
        await updateRecommendationProducts(userEmail, parameterStore);
    };

    const getWalkthroughClientData = async (userEmail: string) => {
        try {
            RootStore.uiStore.showSnackbar('Getting walkthrough data...', 'info');
            runInAction(() => {
                parameterStore.loadingClientData = true;
            });

            const [{data, walkthroughState}, customerQuoteInfo, customerPortalData] = await Promise.all([
                CRM.getCRMWalkthroughData(userEmail),
                CustomerPortalAPI.GetCustomerQuotes(userEmail),
                CustomerPortalAPI.GetCustomerPortalData(userEmail),
            ]);

            if (!customerQuoteInfo) {
                throw new Error('Failed to fetch qualifiedProducts');
            }

            setCustomerQuoteData(customerQuoteInfo);
            const c = convertWalkthroughData(
                data,
                walkthroughState,
                customerPortalData?.customerByEmail,
                customerQuoteInfo,
            );

            const prequal = parameterStore?.prequal;
            const health = parameterStore?.health;
            const quote = parameterStore?.quote;
            const quoteCombination = parameterStore?.quoteCombination;
            const approval = parameterStore?.approval;

            runInAction(() => {
                if (parameterStore) {
                    Object.keys(c).forEach((key) => {
                        if (key === 'walkthroughData') {
                            if (c[key] !== undefined) {
                                runInAction(() => {
                                    const obj = c[key];
                                    parameterStore.walkthroughData = obj;
                                });
                            }
                        } else {
                            if (prequal.hasOwnProperty(key)) {
                                if (c[key] !== undefined) {
                                    parameterStore.onValueChanged(
                                        null,
                                        prequal[key as keyof typeof prequal] as Parameter,
                                        c[key],
                                    );
                                }
                            }
                            if (health.hasOwnProperty(key)) {
                                if (c[key] !== undefined) {
                                    parameterStore.onValueChanged(
                                        null,
                                        health[key as keyof typeof health] as Parameter,
                                        c[key],
                                    );
                                }
                            }
                            if (quote.hasOwnProperty(key)) {
                                if (c[key] !== undefined) {
                                    parameterStore.onValueChanged(
                                        null,
                                        quote[key as keyof typeof quote] as Parameter,
                                        c[key],
                                    );
                                }
                            }
                            if (quoteCombination.isCombination && quoteCombination.hasOwnProperty(key)) {
                                if (c[key] !== undefined) {
                                    parameterStore.onValueChanged(
                                        null,
                                        quoteCombination[key as keyof typeof quoteCombination] as Parameter,
                                        c[key],
                                    );
                                }
                            }
                            if (approval.hasOwnProperty(key)) {
                                if (c[key] !== undefined) {
                                    parameterStore.onValueChanged(
                                        null,
                                        approval[key as keyof typeof approval] as Parameter,
                                        c[key],
                                    );
                                }
                            }
                        }
                    });
                    parameterStore.loadingClientData = false;
                }
            });

            RootStore.uiStore.showSnackbar('Walkthrough data loaded!', 'success');
        } catch (e) {
            runInAction(() => {
                if (parameterStore) {
                    parameterStore.loadingClientData = false;
                }
            });
            console.log(e);
        }
    };

    useEffect(() => {
        if (email && formId) {
            getForm();
        } else if (familyEmail && familyFormId) {
            getFormForFamily();
        }
    }, []);

    useEffect(() => {
        // populate form with walkthrough data only before it is saved
        if (
            contactStore.contact &&
            ((parameterStore.instantIulStartedByCustomer && !parameterStore.instantIulWorkedOnByAgent) ||
                (!email && !(familyEmail || familyFormId)))
        ) {
            getWalkthroughClientData(contactStore.contact);
            parameterStore.instantIulWorkedOnByAgent = true;
        }
    }, [
        contactStore.contact,
        contactStore.id,
        parameterStore.instantIulWorkedOnByAgent,
        parameterStore.instantIulStartedByCustomer,
    ]);

    useEffect(() => {
        // refresh products list at page load and when form is saved
        // Also refresh when walkthrough data finishes loading to ensure we have complete data
        if (contactStore.contact && !parameterStore.loadingClientData) {
            getQualifiedProductsInternal(contactStore.contact);
        }
    }, [contactStore.contact, parameterStore.updatedTimestamp, parameterStore.loadingClientData]);

    useEffect(() => {
        return when(
            () => !parameterStore.loadingClientData, // Only run the effect when the loadingClientData mutated to false
            () => {
                setIsLoading(false);
            },
        );
    }, []);

    const approvalData = useMemo(() => {
        if (contactStore.form?.approval) {
            return contactStore.form.approval.reduce((carry: Record<string, unknown>, a: {p: string; v: unknown}) => ({
                ...carry,
                [a.p]: a.v,
            }));
        }

        return {};
    }, [contactStore.form]);

    const onPreapprovalValueChanged = (...args: Parameters<typeof parameterStore.onValueChangedPreApproval>) => {
        if (familyFormId || familyEmail) {
            const [p, value] = args;

            if (p && p.name === 'sameOwner' && !value) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const keyMap: any = {
                    ...OWNER_KEYS_MAP,
                };

                if (approvalData.validDriversLicense === 'Yes') {
                    keyMap.driversLicenseState = 'ownerIssueState';
                    keyMap.driversLicenseNumber = 'ownerNumber';
                    keyMap.driversLicenseExpiration = 'ownerExpiration';
                }

                if (approvalData.validDriversLicense === 'StateId') {
                    keyMap.stateIdState = 'ownerIssueState';
                    keyMap.stateIdNumber = 'ownerNumber';
                    keyMap.stateIdExpiration = 'ownerExpiration';
                }

                Object.entries(keyMap).forEach(([key, value]) => {
                    // const p = all?.find((p) => p.name === value);
                    const p = parameterStore.approval[value as keyof typeof parameterStore.approval];
                    parameterStore.onValueChangedPreApproval(p, approvalData[key]);
                });

                const p = parameterStore.approval.policyOwnerName;
                parameterStore.onValueChangedPreApproval(p, contactStore.name);
            }
        }
        parameterStore.onValueChangedPreApproval(...args);
    };

    return (
        <Box display="flex" flexDirection="column" flex="1">
            {isLoading ? (
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    py="30px"
                    gridGap="10px"
                >
                    <CircularProgress />
                    <Typography variant="subtitle1" color="textSecondary">
                        Loading form data...
                    </Typography>
                </Box>
            ) : (
                <>
                    <ContactForm store={store} />
                    <CustomerPortalSelectionDialog
                        store={store instanceof AgentStore ? store : RootStore.agentPortalStore}
                    />
                    <Box marginX="30px" display="flex" flexDirection="column" flex="1">
                        <TabPanel style={{overflow: 'none'}} value={selectedTabIndex} index={0}>
                            <PrequalPanel
                                parameterStore={parameterStore}
                                contactsFormStore={contactStore}
                                valueChanged={parameterStore.onValueChangedPreQual}
                                customerQuoteData={customerQuoteData}
                            />
                        </TabPanel>
                        <TabPanel style={{overflow: 'none'}} value={selectedTabIndex} index={1}>
                            <QuotePanel parameterStore={parameterStore} customerPortalStore={customerPortalStore} />
                        </TabPanel>
                        <TabPanel style={{overflow: 'none'}} value={selectedTabIndex} index={2}>
                            <PreApprovalPanel
                                valueChanged={onPreapprovalValueChanged}
                                parameterStore={parameterStore}
                                formDirty={formDirty.get('preapproval')}
                            />
                        </TabPanel>
                        <TabPanel style={{overflow: 'none'}} value={selectedTabIndex} index={3}>
                            <UnderwritingPanel customerPortalStore={customerPortalStore} />
                        </TabPanel>
                        <TabPanel value={selectedTabIndex} index={4}>
                            <BankingPanel customerPortalStore={customerPortalStore} />
                        </TabPanel>
                        <TabPanel
                            value={selectedTabIndex}
                            index={5}
                            display="flex"
                            flex="1"
                            flexDirection="column"
                            flatten
                        >
                            <PolicyPanel customer={customerPortalStore.selectedCustomer as any} />
                        </TabPanel>
                    </Box>
                </>
            )}
        </Box>
    );
});

export const FormsPanel = observer(
    ({filter, forms}: {filter: 'all' | 'qual' | 'approval'; forms: Array<Form> | null}) => {
        if (forms == null || !forms.length) {
            return null;
        }

        console.log('FormsPanel', performance.now());

        var formList: Array<Form>;

        if (filter == 'approval') {
            formList = forms.filter((f) => f.v.formSubmitted);
        } else if (filter == 'qual') {
            formList = forms.filter((f) => f.v.formPreQual);
        } else {
            formList = forms;
        }

        return (
            <div>
                <TableContainer component={Paper}>
                    <Table style={{minWidth: '100%'}}>
                        <TableHead>
                            <TableRow aria-label="form-table-headers">
                                <TableCell aria-label="form-table-date">Date</TableCell>
                                <TableCell aria-label="form-table-agent">Agent</TableCell>
                                <TableCell aria-label="form-table-contact">Client Name</TableCell>
                                <TableCell aria-label="form-table-applicant-name">Contact</TableCell>
                                <TableCell aria-label="form-table-form-name">Form</TableCell>
                                <TableCell aria-label="form-table-pre-qual">Pre-Qualification</TableCell>
                                <TableCell aria-label="form-table-pre-approval">Pre-Approval</TableCell>
                                <TableCell aria-label="form-table-control">Control</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {formList?.map((f, i) => {
                                if (f == null) {
                                    return null;
                                }
                                return (
                                    <TableRow aria-label={`form-row`} key={f.p + f.v.contact + f.v.form + i}>
                                        <TableCell aria-label={`${i}-form-date`}>{f.p}</TableCell>
                                        <TableCell aria-label={`${i}-form-agent`}>{f.v.agent}</TableCell>
                                        <TableCell aria-label={`${i}-form-contact`}>{f.v.contact}</TableCell>
                                        <TableCell aria-label={`${i}-form-applicant-full-name`}>
                                            {f.v.applicantFirstName} {f.v.applicantLastName}
                                        </TableCell>
                                        <TableCell aria-label={`${i}-form-name`}>
                                            {f.v.name} <small>({f.v.form})</small>
                                        </TableCell>

                                        <TableCell aria-label={`${i}-form-pre-qual`}>
                                            {f.v.formPreQual ? 'YES' : 'NO'}
                                        </TableCell>
                                        <TableCell aria-label={`${i}-form-pre-approval`}>
                                            {f.v.formPreApproval ? 'YES' : 'NO'}
                                        </TableCell>

                                        <TableCell aria-label={`${i}-form-contact}`}>
                                            <FormDialog contact={f.v.contact} form={f.v.form}></FormDialog>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        );
    },
);

export const FormDialog = observer(({contact, form}: {contact: string; form: string}) => {
    const [refresh] = useState(false);
    const history = useHistory();

    const [open, setOpen] = useState(false);
    const handleClose = () => {
        setOpen(false);
    };

    const caseManagerStore = RootStore.caseManagerStore;

    const handleClickOpen = async () => {
        RootStore.uiStore.showSnackbar('Getting form...', 'info');
        RootStore.assistStore.resetStore();
        history.push('/case-manager/form/' + contact + '/' + form);
    };

    return (
        <div>
            {contact == null || contact == '' || form == null || form == '' ? (
                <div>not a valid form</div>
            ) : (
                <Button
                    aria-label={`${form}-open`}
                    variant="outlined"
                    color="primary"
                    style={{marginTop: 12}}
                    onClick={handleClickOpen}
                >
                    Open Form
                </Button>
            )}

            <Dialog aria-label={`${form}-dialog`} fullScreen open={open} onClose={handleClose}>
                <AppBar style={{position: 'relative'}}>
                    <Toolbar>
                        <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
                            <CloseIcon />
                        </IconButton>
                        <Typography variant="h6" style={{marginLeft: 2, flex: 1}}>
                            Form: {contact}
                        </Typography>
                        <Button autoFocus color="inherit" onClick={handleClose}>
                            Close
                        </Button>
                    </Toolbar>
                </AppBar>
                <Grid container spacing={3}>
                    <Grid item xs={12} style={{padding: 30}}>
                        <FormDataPanel selectedTabIndex={0} key={refresh ? 'Y' : 'N'} store={caseManagerStore} />
                    </Grid>
                </Grid>
            </Dialog>
        </div>
    );
});
